import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/shared/ui/Loader";
import type { MarketingPushMonitorResponse } from "@/shared/notifications/marketing-push-monitor.types";
import { RefreshCwIcon } from "lucide-react";

function fmt(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("lo-LA");
  } catch {
    return iso;
  }
}

function roleLabel(role: string): string {
  switch (role) {
    case "target":
      return "ຈະໄດ້ຮັບ";
    case "excluded_no_token":
      return "ບໍ່ມີ FCM";
    case "excluded_branch":
      return "ສາຂາບໍ່ຕົງ";
    default:
      return role;
  }
}

function roleVariant(
  role: string,
): "default" | "secondary" | "outline" | "destructive" {
  switch (role) {
    case "target":
      return "default";
    case "excluded_branch":
      return "secondary";
    default:
      return "outline";
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case "sent":
      return "ສົ່ງສຳເລັດ";
    case "invalid_token":
      return "token ໃຊ້ບໍ່ໄດ້";
    case "fcm_error":
      return "FCM ຜິດພາດ";
    case "skipped_no_credentials":
      return "Firebase key ບໍ່ພ້ອມ";
    case "skipped_no_project":
      return "ບໍ່ມີ project ID";
    default:
      return status;
  }
}

type Props = {
  title?: string;
  data: MarketingPushMonitorResponse | undefined;
  isLoading: boolean;
  isFetching: boolean;
  onRefresh: () => void;
};

export function MarketingPushMonitorPanel({
  title = "ຕິດຕາມ push ແອັບລູກຄ້າ",
  data,
  isLoading,
  isFetching,
  onRefresh,
}: Props) {
  if (isLoading && !data) {
    return (
      <Card className="rounded-xl border">
        <CardContent className="flex justify-center py-10">
          <Loader />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const latestJob = data.jobs[0];
  const latestDelivery = latestJob?.delivery;

  return (
    <Card className="rounded-xl border">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-lg">{title}</CardTitle>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isFetching}
        >
          <RefreshCwIcon
            className={`mr-1 size-4 ${isFetching ? "animate-spin" : ""}`}
          />
          ຟື້ນຟາຍ
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-muted-foreground">ສົ່ງ push ເມື່ອ</p>
            <p className="font-medium">{fmt(data.pushSentAt)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">ຄິວ worker</p>
            <p className="font-medium">
              {data.hasPendingOutbox ? (
                <Badge variant="secondary">ລໍຖ້າສົ່ງ</Badge>
              ) : (
                <Badge variant="outline">ບໍ່ມີຄິວຄ້າງ</Badge>
              )}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground">ເປົ້າໝາຍ (ຈະໄດ້ຮັບ)</p>
            <p className="font-medium">{data.audienceSummary.target} ຄົນ</p>
          </div>
          <div>
            <p className="text-muted-foreground">ບໍ່ເຂົ້າເງື່ອນໄຂ</p>
            <p className="font-medium">
              {data.audienceSummary.excludedNoToken} ບໍ່ມີ token ·{" "}
              {data.audienceSummary.excludedBranch} ສາຂາບໍ່ຕົງ
            </p>
          </div>
        </div>

        {latestDelivery ? (
          <div className="rounded-lg border bg-muted/30 p-3 text-sm">
            <p className="mb-2 font-medium">ຜົນລ່າສຸດ (worker)</p>
            <p>
              ສົ່ງສຳເລັດ {latestDelivery.summary.sent} /{" "}
              {latestDelivery.summary.targeted} · ລົ້ມເຫຼວ{" "}
              {latestDelivery.summary.failed}
              {latestDelivery.summary.skippedCredentials
                ? " · Firebase credentials ບໍ່ພ້ອມ"
                : null}
            </p>
            {latestDelivery.pipelineNotes.length > 0 ? (
              <p className="mt-1 text-muted-foreground">
                {latestDelivery.pipelineNotes.join(", ")}
              </p>
            ) : null}
          </div>
        ) : latestJob?.pending ? (
          <p className="text-sm text-muted-foreground">
            ມີຄິວສົ່ງ — ລໍ worker (~2 ວິ) …
          </p>
        ) : null}

        <div>
          <p className="mb-2 text-sm font-medium">ລູກຄ້າ (ພິມຄຳສັ່ງປັດຈຸບັນ)</p>
          <div className="max-h-64 overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ຊື່ / ເບີ</TableHead>
                  <TableHead>ສາຂາ</TableHead>
                  <TableHead>Token</TableHead>
                  <TableHead>ສະຖານະ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.audience.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-muted-foreground">
                      ບໍ່ມີລູກຄ້າໃນລະບົບ
                    </TableCell>
                  </TableRow>
                ) : (
                  data.audience.map((row) => (
                    <TableRow key={row.customerId}>
                      <TableCell>
                        <div>{row.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {row.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        {row.branchName ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {row.tokenPrefix ?? "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={roleVariant(row.role)}>
                          {roleLabel(row.role)}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {latestDelivery && latestDelivery.recipients.length > 0 ? (
          <div>
            <p className="mb-2 text-sm font-medium">
              ຜົນສົ່ງຄັ້ງລ່າສຸດ (ລາຍຄົນ)
            </p>
            <div className="max-h-48 overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ລູກຄ້າ</TableHead>
                    <TableHead>ຜົນ</TableHead>
                    <TableHead>ໝາຍເຫດ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {latestDelivery.recipients.map((r) => (
                    <TableRow key={`${r.userId}-${r.tokenPrefix}`}>
                      <TableCell>{r.label}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            r.status === "sent" ? "default" : "destructive"
                          }
                        >
                          {statusLabel(r.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground">
                        {r.errorMessage ?? r.errorCode ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}

        <div>
          <p className="mb-2 text-sm font-medium">ປະຫວັດຄິວ (outbox)</p>
          <div className="max-h-40 overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ເວລາເຂົ້າຄິວ</TableHead>
                  <TableHead>ເວລາສົ່ງຈົບ</TableHead>
                  <TableHead>ສະຖານະ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.jobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-muted-foreground">
                      ຍັງບໍ່ມີຄິວ push
                    </TableCell>
                  </TableRow>
                ) : (
                  data.jobs.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell className="text-xs">
                        {fmt(job.createdAt)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {fmt(job.processedAt)}
                      </TableCell>
                      <TableCell>
                        {job.pending ? (
                          <Badge variant="secondary">ຄ້າງ</Badge>
                        ) : job.delivery ? (
                          <Badge variant="outline">
                            {job.delivery.summary.sent}/
                            {job.delivery.summary.targeted} OK
                          </Badge>
                        ) : (
                          <Badge variant="outline">ຈົບ (ບໍ່ມີລາຍງານ)</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
